import { FC } from 'react';
import { VoucherPaymentStatus } from 'src/models/VoucherPayment.interface';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import Status from 'src/components/Status';
import { DATE_FORMATS } from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import {
  getPaymentModeText,
  getVoucherPaymentStatusText,
  getChequeStatusText,
} from 'src/utils/status.utils';
import {
  VoucherPaymentViewStyledDialog,
  VoucherPaymentFixedHeaderWrapper,
  VoucherPaymentContentWrapper,
  VoucherPaymentHeaderSection,
  VoucherPaymentHeaderLeft,
  VoucherPaymentViewTitleTypography,
  VoucherPaymentViewCloseIcon,
  VoucherPaymentMainContentGrid,
  VoucherPaymentDetailsBox,
  VoucherPaymentInfoRow,
  VoucherPaymentInfoBlock,
  VoucherPaymentInfoBlockRight,
  VoucherPaymentMethodCard,
  VoucherPaymentMethodRow,
  VoucherPaymentMethodDivider,
  VoucherPaymentStatusInfoBox,
  VoucherPaymentStatusInfoRow,
  VoucherPaymentRejectionNoteBox,
  VoucherPaymentRejectionLabelTypography,
  VoucherPaymentLabelTypography,
  VoucherPaymentValueTypography,
  VoucherPaymentSmallValueTypography,
  VoucherPaymentDetailLabelText,
  VoucherPaymentDetailValueText,
  VoucherPaymentNoteText,
} from './VoucherPaymentDialog.styles';
import { ViewVoucherPaymentDialogProps } from 'src/models/VoucherPayment.interface';

const ViewVoucherPaymentDialog: FC<ViewVoucherPaymentDialogProps> = ({
  open,
  onClose,
  payment,
}) => {
  if (!payment) return null;

  const paymentModeValue =
    typeof payment.paymentMode === 'string'
      ? payment.paymentMode
      : payment.paymentMode;
  const isCheque =
    paymentModeValue === PaymentMode.CHEQUE || paymentModeValue === 'Cheque';
  const isOnline =
    paymentModeValue === PaymentMode.ONLINE || paymentModeValue === 'Online';
  const isRejected =
    payment.status === VoucherPaymentStatus.REJECTED ||
    payment.status === 'Rejected';
  const isApproved =
    payment.status === VoucherPaymentStatus.APPROVED ||
    payment.status === 'Approved';

  return (
    <VoucherPaymentViewStyledDialog open={open} onClose={onClose}>
      <VoucherPaymentFixedHeaderWrapper>
        <VoucherPaymentHeaderSection>
          <VoucherPaymentHeaderLeft>
            <VoucherPaymentViewTitleTypography>
              Voucher Payment Details
            </VoucherPaymentViewTitleTypography>
            <Status status={getVoucherPaymentStatusText(payment.status)} />
          </VoucherPaymentHeaderLeft>
          <VoucherPaymentViewCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={onClose}
          />
        </VoucherPaymentHeaderSection>
      </VoucherPaymentFixedHeaderWrapper>

      <VoucherPaymentContentWrapper>
        <VoucherPaymentMainContentGrid>
          {/* Left Section - Payment Details */}
          <VoucherPaymentDetailsBox>
            {/* Date and Amount Row */}
            <VoucherPaymentInfoRow>
              <VoucherPaymentInfoBlock>
                <VoucherPaymentLabelTypography>
                  Date
                </VoucherPaymentLabelTypography>
                <VoucherPaymentValueTypography>
                  {new Date(payment.paymentDate).toLocaleDateString(
                    DATE_FORMATS.LOCALE
                  )}
                </VoucherPaymentValueTypography>
              </VoucherPaymentInfoBlock>
              <VoucherPaymentInfoBlockRight>
                <VoucherPaymentLabelTypography>
                  Amount (LKR)
                </VoucherPaymentLabelTypography>
                <VoucherPaymentValueTypography>
                  {formatCurrency(payment.paidAmount)}
                </VoucherPaymentValueTypography>
              </VoucherPaymentInfoBlockRight>
            </VoucherPaymentInfoRow>

            {/* Receiver Name Row */}
            <VoucherPaymentInfoRow>
              <VoucherPaymentInfoBlock>
                <VoucherPaymentLabelTypography>
                  Receiver Name
                </VoucherPaymentLabelTypography>
                <VoucherPaymentValueTypography>
                  {payment.receiverName || '-'}
                </VoucherPaymentValueTypography>
              </VoucherPaymentInfoBlock>
            </VoucherPaymentInfoRow>

            {/* Payment Mode Row */}
            <VoucherPaymentInfoRow>
              <VoucherPaymentInfoBlock>
                <VoucherPaymentLabelTypography>
                  Payment Mode
                </VoucherPaymentLabelTypography>
                <VoucherPaymentValueTypography>
                  {typeof payment.paymentMode === 'string'
                    ? payment.paymentMode
                    : getPaymentModeText(payment.paymentMode)}
                </VoucherPaymentValueTypography>
              </VoucherPaymentInfoBlock>
            </VoucherPaymentInfoRow>

            {/* Cheque Details Card */}
            {isCheque && (
              <VoucherPaymentMethodCard>
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Cheque Date
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {payment.chequeDate
                      ? new Date(payment.chequeDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
                <VoucherPaymentMethodDivider />
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Cheque Number
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {payment.chequeNumber || '-'}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
                <VoucherPaymentMethodDivider />
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Cheque Status
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {getChequeStatusText(payment.chequeStatus)}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
                {payment.chequeRealizedDate && (
                  <>
                    <VoucherPaymentMethodDivider />
                    <VoucherPaymentMethodRow>
                      <VoucherPaymentDetailLabelText>
                        Cheque Realized Date
                      </VoucherPaymentDetailLabelText>
                      <VoucherPaymentDetailValueText>
                        {new Date(
                          payment.chequeRealizedDate
                        ).toLocaleDateString(DATE_FORMATS.LOCALE)}
                      </VoucherPaymentDetailValueText>
                    </VoucherPaymentMethodRow>
                  </>
                )}
                <VoucherPaymentMethodDivider />
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Bank Account
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {payment.bankAccountName || '-'}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
              </VoucherPaymentMethodCard>
            )}

            {/* Online Payment Details Card */}
            {isOnline && (
              <VoucherPaymentMethodCard>
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Reference Number
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {payment.paymentRefNo || '-'}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
                <VoucherPaymentMethodDivider />
                <VoucherPaymentMethodRow>
                  <VoucherPaymentDetailLabelText>
                    Bank Account
                  </VoucherPaymentDetailLabelText>
                  <VoucherPaymentDetailValueText>
                    {payment.bankAccountName || '-'}
                  </VoucherPaymentDetailValueText>
                </VoucherPaymentMethodRow>
              </VoucherPaymentMethodCard>
            )}
          </VoucherPaymentDetailsBox>

          {/* Right Section - Status Info */}
          {isRejected && (
            <VoucherPaymentStatusInfoBox>
              <VoucherPaymentStatusInfoRow>
                <VoucherPaymentInfoBlock>
                  <VoucherPaymentLabelTypography>
                    Rejected Date
                  </VoucherPaymentLabelTypography>
                  <VoucherPaymentSmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </VoucherPaymentSmallValueTypography>
                </VoucherPaymentInfoBlock>
                <VoucherPaymentInfoBlockRight>
                  <VoucherPaymentLabelTypography>
                    Rejected User
                  </VoucherPaymentLabelTypography>
                  <VoucherPaymentSmallValueTypography>
                    {payment.approvedBy || '-'}
                  </VoucherPaymentSmallValueTypography>
                </VoucherPaymentInfoBlockRight>
              </VoucherPaymentStatusInfoRow>
              <VoucherPaymentRejectionNoteBox>
                <VoucherPaymentRejectionLabelTypography>
                  Rejection Note
                </VoucherPaymentRejectionLabelTypography>
                <VoucherPaymentNoteText>
                  {payment.rejectionNotes || '-'}
                </VoucherPaymentNoteText>
              </VoucherPaymentRejectionNoteBox>
            </VoucherPaymentStatusInfoBox>
          )}

          {isApproved && (
            <VoucherPaymentStatusInfoBox>
              <VoucherPaymentStatusInfoRow>
                <VoucherPaymentInfoBlock>
                  <VoucherPaymentLabelTypography>
                    Approved Date
                  </VoucherPaymentLabelTypography>
                  <VoucherPaymentSmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </VoucherPaymentSmallValueTypography>
                </VoucherPaymentInfoBlock>
                <VoucherPaymentInfoBlockRight>
                  <VoucherPaymentLabelTypography>
                    Approved By
                  </VoucherPaymentLabelTypography>
                  <VoucherPaymentSmallValueTypography>
                    {payment.approvedBy || '-'}
                  </VoucherPaymentSmallValueTypography>
                </VoucherPaymentInfoBlockRight>
              </VoucherPaymentStatusInfoRow>
            </VoucherPaymentStatusInfoBox>
          )}
        </VoucherPaymentMainContentGrid>
      </VoucherPaymentContentWrapper>
    </VoucherPaymentViewStyledDialog>
  );
};

export default ViewVoucherPaymentDialog;
